import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to safely parse JSON from localStorage with retry
const getStoredUser = () => {
  try {
    // Force a fresh read from localStorage
    const stored = window.localStorage.getItem('user');
    if (!stored) return null;
    return JSON.parse(stored);
  } catch (e) {
    console.error('Error parsing stored user:', e);
    return null;
  }
};

// Helper to get token with fresh read
const getStoredToken = () => {
  try {
    return window.localStorage.getItem('token') || null;
  } catch (e) {
    console.error('Error reading token:', e);
    return null;
  }
};

export function AuthProvider({ children }) {
  // Initialize from localStorage - force fresh read
  const [user, setUser] = useState(() => getStoredUser());
  const [token, setToken] = useState(() => getStoredToken());
  const [loading, setLoading] = useState(true);

  // Re-check localStorage on mount and focus (handles mobile browser quirks)
  useEffect(() => {
    const checkStorage = () => {
      const storedToken = getStoredToken();
      const storedUser = getStoredUser();
      
      if (storedToken && storedUser && !user) {
        console.log('Found auth data in localStorage, updating state');
        setToken(storedToken);
        setUser(storedUser);
      }
    };
    
    // Check immediately
    checkStorage();
    
    // Also check when window gains focus (mobile tab switching)
    window.addEventListener('focus', checkStorage);
    
    // Check after a small delay (mobile browser quirk)
    const timeout = setTimeout(checkStorage, 100);
    
    return () => {
      window.removeEventListener('focus', checkStorage);
      clearTimeout(timeout);
    };
  }, [user]);

  useEffect(() => {
    const initAuth = async () => {
      // Skip auth check if we have a session_id in the hash (Google callback)
      if (window.location.hash.includes('session_id')) {
        setLoading(false);
        return;
      }
      
      // Get fresh values from localStorage
      const currentToken = getStoredToken();
      const storedUser = getStoredUser();
      
      // If we have both token and user from localStorage, we're authenticated
      if (currentToken && storedUser) {
        setToken(currentToken);
        setUser(storedUser);
        setLoading(false);
        
        // Validate token in background (don't block UI)
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${currentToken}` }
          });
          // Update user data if changed
          setUser(response.data);
          localStorage.setItem('user', JSON.stringify(response.data));
        } catch (error) {
          console.error('Token validation failed:', error);
          // Token is invalid, clear everything
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setToken(null);
          setUser(null);
        }
      } else if (currentToken && !storedUser) {
        // Have token but no user data - need to fetch
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${currentToken}` }
          });
          setUser(response.data);
          localStorage.setItem('user', JSON.stringify(response.data));
        } catch (error) {
          console.error('Auth init error:', error);
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setToken(null);
          setUser(null);
        }
        setLoading(false);
      } else {
        setLoading(false);
      }
    };
    
    initAuth();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    
    // Write to localStorage synchronously
    localStorage.setItem('token', newToken);
    localStorage.setItem('user', JSON.stringify(userData));
    
    // Update state
    setToken(newToken);
    setUser(userData);
    
    return userData;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name });
    const { token: newToken, user: userData } = response.data;
    
    // Write to localStorage synchronously
    localStorage.setItem('token', newToken);
    localStorage.setItem('user', JSON.stringify(userData));
    
    // Update state
    setToken(newToken);
    setUser(userData);
    
    return userData;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
  };

  const updateUser = (userData) => {
    setUser(userData);
    if (userData) {
      localStorage.setItem('user', JSON.stringify(userData));
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        setToken(storedToken);
      }
    }
  };

  const setAuthToken = (newToken) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
  };

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${token}`
  });

  // Calculate isAuthenticated from current state
  const isAuthenticated = !!(user && token);

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      register,
      logout,
      updateUser,
      setAuthToken,
      getAuthHeaders,
      isAuthenticated
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
